/** The "Coming up" reads Home draws, named once so Home and the background warm
 *  share one cache entry per actor and workspace. Home redraws the last answer
 *  at once and refreshes it on every visit and at each change of day. */

import { lolerMachines } from '../../shared-ui/home/homePlant';
import { WorkerCardsService } from '../../services/WorkerCardsService';
import { PlantAssetsService } from '../../services/PlantAssetsService';
import { CISService } from '../../services/CISService';
import { SubcontractorCoverService } from '../../services/SubcontractorCoverService';
import { RfiService } from '../../services/RfiService';
import { DayworkService } from '../../services/DayworkService';
import type { UnsignedDayworkTally } from '../../contract-administration/dayworkSignOff';
import { ExpensesService } from '../../services/ExpensesService';
import { InvoicesService } from '../../services/InvoicesService';
import { listLatestInvoiceChases } from '../../services/InvoiceChaseService';
import { OVERDUE_INVOICE_STATUSES, type InvoiceChaseStamp, type OverdueInvoiceRead } from './overdueInvoiceRow';
import { fetchWorkspaceEnquiries } from '../../services/workspaceEnquiryService';
import type { WorkspaceDeal } from '../pipeline/types';
import type { ExpiringWorkerCard } from '../../types/workerCards';
import type { ExpiringSubcontractorCover } from '../subcontractors/subcontractorCover';
import type { RfiRecord } from '../../types/rfis';
import { issuedRfis } from '../../shared-ui/home/homeRfis';
import type { PlantAsset } from '../../types/plant';
import type { CISReturn, ExpenseClaim, Invoice } from '../../types/accounting';

const SITE_CARD_WINDOW_DAYS = 30;

export interface HomeRead<T> { name: string; load: () => Promise<T> }

export function localDayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// CIS and expense deadlines flip at UK midnight (see cisDueCue), so their key must too.
export function londonDayKey(): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London' }).format(new Date());
}

// The paged upcoming response: the unpaged one is capped at 500 rows
// oldest-first, so a long history of old cards could hide every current one.
export const expiringSiteCardsRead = (workspaceId: string): HomeRead<ExpiringWorkerCard[]> => ({
  name: 'home/site-cards',
  load: () => WorkerCardsService.listExpiring(workspaceId, SITE_CARD_WINDOW_DAYS, true),
});

// The same 60-day window the Subcontractors register draws; the row builder narrows it to Home's own window.
export const expiringSubcontractorCoverRead = (workspaceId: string): HomeRead<ExpiringSubcontractorCover[]> => ({
  name: 'home/subcontractor-cover',
  load: () => SubcontractorCoverService.listExpiring(workspaceId, 60),
});

export const lolerPlantAssetsRead = (workspaceId: string): HomeRead<PlantAsset[]> => ({
  name: 'home/loler-plant',
  load: async () => lolerMachines(await PlantAssetsService.list(workspaceId)),
});

export const draftCisReturnsRead = (workspaceId: string): HomeRead<CISReturn[]> => ({
  name: 'home/cis-drafts',
  load: async () => (await CISService.list({ workspaceId })).filter((r) => r.status === 'draft'),
});

export const submittedExpenseClaimsRead = (workspaceId: string): HomeRead<ExpenseClaim[]> => ({
  name: 'home/expense-claims',
  load: () => ExpensesService.list({ workspaceId, status: 'submitted' }),
});

export type { OverdueInvoiceRead };

// Only the three unpaid statuses are asked for, so a long paid history is never paged through for Home.
// A failed chase read leaves the chases out: the row then over-reports rather than vanishing.
export const unpaidSalesInvoicesRead = (workspaceId: string): HomeRead<OverdueInvoiceRead> => ({
  name: 'home/sales-invoices-unpaid',
  load: async () => {
    const [lists, chases] = await Promise.all([
      Promise.all(OVERDUE_INVOICE_STATUSES.map((status) => InvoicesService.list({ workspaceId, type: 'outbound', status }))),
      listLatestInvoiceChases(workspaceId).catch(() => []),
    ]);
    return { invoices: lists.flat(), chases };
  },
});

// The server leaves out a project with nothing waiting, so the map holds only projects worth a look.
export const unsignedDayworksRead = (workspaceId: string): HomeRead<Record<string, UnsignedDayworkTally>> => ({
  name: 'home/dayworks-unsigned',
  load: () => DayworkService.unsigned(workspaceId),
});

// Only issued RFIs can be overdue, so the rest never leave the read.
export const issuedRfisRead = (): HomeRead<RfiRecord[]> => ({
  name: 'home/rfis-issued',
  load: async () => issuedRfis(await RfiService.list()),
});

// Only the two open stages carry a live tender date, so the rest never leave the read.
export const enquiryDealsRead = (workspaceId: string): HomeRead<WorkspaceDeal[]> => ({
  name: 'home/enquiry-deals',
  load: async () => (await fetchWorkspaceEnquiries(workspaceId)).filter((d) => d.tender_deadline !== null),
});

/** The Enquiries board is a navigation grant, not a role. Until grants are authoritative only customer accounts have it. */
export function enquiriesBoardGranted(
  navigation: { authoritative: boolean; navigation: { main: readonly { id: string }[] } | null },
  isClient: boolean,
): boolean {
  return navigation.authoritative ? navigation.navigation?.main.some((item) => item.id === 'workspace-pipeline') === true : isClient;
}

interface HomeReader {
  isAdmin(): boolean;
  isStaff(): boolean;
  isClient(): boolean;
  isSupervisor(): boolean;
  user?: {
    role?: string;
    active_workspace_id?: string | null;
    activeWorkspaceId?: string | null;
    workspaceId?: string | null;
    workspace_id?: string | null;
  } | null;
}

/** Which workspace each "Coming up" read uses for this person, or none. */
export function homeReadWorkspaces(auth: HomeReader): { siteCards?: string; cis?: string; expenses?: string; rfis?: string; dayworks?: string } {
  const user = auth.user;
  const own = user?.workspaceId ?? user?.workspace_id ?? undefined;
  const owner = user?.role === 'workspace_owner' || user?.role === 'workspace_admin';
  return {
    // Only the operations Home (QSDashboard) lists site cards and plant, for the
    // workspace it passes. The server refuses site cards to non-admins anyway.
    siteCards: auth.isClient() || auth.isSupervisor() ? undefined : user?.active_workspace_id ?? user?.activeWorkspaceId ?? own,
    // Same roles that may open CIS Returns. The workspace is the user's own, not the
    // active one, because CISReturnsView reads currentUser.workspaceId and a row
    // must open the list it came from.
    cis: auth.isAdmin() || auth.isStaff() || owner ? own : undefined,
    // Only the people who can approve a claim are nudged about one.
    expenses: owner ? own : undefined,
    // Same roles as CIS Returns: not clients, not Field-only members.
    rfis: auth.isAdmin() || auth.isStaff() || owner ? own : undefined,
    // Same people as the Projects list column; the server narrows the tallies to projects the caller may read.
    dayworks: auth.isClient() || auth.isSupervisor() ? undefined : user?.active_workspace_id ?? user?.activeWorkspaceId ?? own,
  };
}
